import { type FC, useEffect, useState } from "react";

import { clsx } from "@alpacahq/ui/old";

interface CountdownButtonProps {
  onConfirm: () => Promise<void>;
  buttonText?: string;
  disabled?: boolean;
  countdownSeconds?: number;
}

// My way of adding a confirm step without altering the existing UI lol
export const CountdownButton: FC<CountdownButtonProps> = ({
  onConfirm,
  buttonText = "Confirm",
  disabled = false,
  countdownSeconds = 3,
}) => {
  const [countdown, setCountdown] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: countdown is the trigger that advances the timer one second at a time. callback is a caller-supplied handler, so listing it would clear and restart the countdown on every render.
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    // Start the countdown if it's not null and greater than 0
    if (countdown !== null && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (countdown === 0) {
      // Execute the callback if the countdown is finished
      callback();
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [countdown]);

  // Update the countdown state to start the countdown
  const start = () => setCountdown(countdownSeconds);

  // Update the countdown state to cancel the countdown
  const cancel = () => setCountdown(null);

  // This is executed after the countdown is finished
  const callback = async () => {
    setIsProcessing(true);

    try {
      await onConfirm();
    } catch (error) {
      console.error("Error during callback execution:", error);
    } finally {
      setIsProcessing(false);
      setCountdown(null);
    }
  };

  return (
    <button
      type="button"
      className={clsx(
        "flex h-10 cursor-pointer items-center justify-center rounded-lg bg-yellow-400 px-4 font-medium text-sm text-yellow-900 transition-all duration-400 ease-in-out hover:bg-yellow-500 disabled:cursor-not-allowed disabled:opacity-50",
        countdown !== null &&
          !isProcessing &&
          "bg-rose-500 text-rose-50 hover:bg-rose-600",
      )}
      onClick={countdown === null ? start : cancel}
      disabled={isProcessing || disabled}
    >
      {isProcessing
        ? "Please wait..."
        : countdown !== null
          ? `Cancel (${countdown})...`
          : buttonText}
    </button>
  );
};
